/**
 * Facilities Field Registry
 * Single source of truth for all facility field definitions
 */

import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';
import { Facility } from '../../services/api/domains/facilities/types';

// Type alias for convenience and type safety
type FacilityFieldDefinition = BaseFieldDefinition<Facility>;

/**
 * Comprehensive field registry for Facilities
 * This is the single source of truth for all field metadata
 */
export const FACILITIES_FIELDS_REGISTRY: FacilityFieldDefinition[] = [
  // ========== Core Information ==========
  {
    key: 'facility_name',
    label: 'Facility Name',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    isRequired: true,
    renderConfig: {
      width: 200,
      flex: 1,
    },
  },
  {
    key: 'manufacturer_name',
    label: 'Manufacturer Name',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 200,
      flex: 1,
    },
    description: 'Related manufacturer (read-only, set via manufacturer_id)',
  },

  // ========== Identification Numbers ==========
  {
    key: 'fei_number',
    label: 'FEI Number',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 120,
    },
  },
  {
    key: 'duns_number',
    label: 'DUNS Number',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 120,
    },
  },

  // ========== Address Information ==========
  {
    key: 'address_line1',
    label: 'Address Line 1',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 200,
    },
  },
  {
    key: 'address_line2',
    label: 'Address Line 2',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 200,
    },
  },
  {
    key: 'city',
    label: 'City',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 150,
    },
  },
  {
    key: 'state_province',
    label: 'State/Province',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 120,
    },
  },
  {
    key: 'postal_code',
    label: 'Postal Code',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 120,
    },
  },
  {
    key: 'country_code',
    label: 'Country Code',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 120,
    },
  },

  // ========== Location Coordinates ==========
  {
    key: 'latitude',
    label: 'Latitude',
    dataType: 'number',
    primitiveType: 'number',
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    isEditable: true,
    renderConfig: {
      width: 100,
    },
  },
  {
    key: 'longitude',
    label: 'Longitude',
    dataType: 'number',
    primitiveType: 'number',
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    isEditable: true,
    renderConfig: {
      width: 100,
    },
  },

  // ========== QTA Information ==========
  {
    key: 'qta_expiration_date',
    label: 'QTA Expiration Date',
    dataType: 'date',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 150,
      formatAs: 'dateonly',
    },
  },

  // ========== Status and Audit ==========
  {
    key: 'facility_status',
    label: 'Vendor Facility Status',
    dataType: 'selection',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    selectionOptions: [
      { value: 'APPROVED', label: 'Approved' },
      { value: 'INACTIVE', label: 'Inactive' },
      { value: 'DISQUALIFY', label: 'Disqualify' },
      { value: 'R&D ONLY', label: 'R&D Only' },
      { value: 'ON HOLD', label: 'On Hold' },
      { value: 'NOT MANUF.', label: 'Not Manuf.' },
    ],
    renderConfig: {
      width: 150,
    },
    description: "Also known as Manufacturer's Status",
  },
  {
    key: 'audit_date',
    label: 'Audit Date',
    dataType: 'date',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 130,
      formatAs: 'dateonly',
    },
  },
  {
    key: 'audit_type',
    label: 'Audit Type',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 120,
    },
  },
  {
    key: 'is_primary',
    label: 'Is Primary',
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    isEditable: true,
    renderConfig: {
      width: 100,
    },
  },

  // ========== Comments ==========
  {
    key: 'comments',
    label: 'Comments',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: true,
    renderConfig: {
      width: 250,
    },
  },

  // ========== Metadata (Read-Only) ==========
  {
    key: 'user_id',
    label: 'User ID',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 150,
    },
  },
  {
    key: 'created_date',
    label: 'Created Date',
    dataType: 'date',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 150,
    },
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    dataType: 'date',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 150,
    },
  },

  // ========== Internal IDs and Counts (not displayed by default) ==========
  {
    key: 'facility_id',
    label: 'Facility ID',
    dataType: 'number',
    primitiveType: 'number',
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 100,
    },
  },
  {
    key: 'manufacturer_id',
    label: 'Manufacturer',
    dataType: 'number',
    primitiveType: 'number',
    isRelation: true,
    isEditable: false,
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'manufacturers',
      idField: 'manufacturer_id',
      displayField: 'manufacturer_name',
      searchField: 'manufacturer_name',
      dataSource: 'api',
      apiEndpoint: '/manufacturers',
    },
    renderConfig: {
      width: 120,
    },
  },
  {
    key: 'manufacturer_facility_count',
    label: 'Manufacturer Facility Count',
    dataType: 'number',
    primitiveType: 'number',
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 150,
    },
    description: 'Count of active facilities for this manufacturer',
  },
];

// Create all helper functions at once using shared factory
const helpers = createFieldHelpers<Facility>(FACILITIES_FIELDS_REGISTRY);

// Export individual helpers for convenient importing
export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
